import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUp,
  AudioLines,
  BarChart3,
  BookOpen,
  Bot,
  Calculator,
  CalendarClock,
  FileText,
  Image,
  Megaphone,
  PenSquare,
  Presentation,
  ShieldCheck,
  Video,
} from 'lucide-react';

import { authManager } from '../services/authManager';

// Adresse de l'équipe OmnIA pour les organisations intéressées. Une variable
// de déploiement la remplace au besoin.
const CONTACT = process.env.REACT_APP_CONTACT_OMNIA || 'malick.douet@efficienceglobale.com';

const carte = 'rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]';

const DEPARTS = [
  {
    icone: PenSquare,
    titre: 'Nouvelle discussion',
    texte:
      "On écrit sa demande comme à un collègue. L'agent adapté est choisi d'après le message, ou désigné par « @ ». Une question hors catalogue trouve toujours une réponse.",
  },
  {
    icone: Megaphone,
    titre: 'Nouvelle communication',
    texte:
      "Affiches, spots radio, capsules vidéo : les agents de communication prennent la demande, dans le ton et les langues de l'organisation.",
  },
  {
    icone: Bot,
    titre: 'Agents',
    texte:
      "Des agents qui travaillent seuls, à date fixe : vérification des données en début de mois, rapport mensuel, rangés là où l'équipe les attend.",
  },
];

const PRODUCTIONS = [
  { icone: FileText, titre: 'Termes de référence', texte: 'Contexte, objectifs, agenda détaillé, participants et budget récapitulatif. Exportés en Word.' },
  { icone: Calculator, titre: 'Budgets et quantification', texte: "Tableur à formules, adossé au barème de coûts unitaires de l'organisation." },
  { icone: BarChart3, titre: 'Analyse des données', texte: "Indicateurs lus dans les systèmes d'information (DHIS2 au PEV), datés et sourcés." },
  { icone: AudioLines, titre: 'Comptes rendus de réunion', texte: 'Transcription, puis compte rendu selon le plan propre à chaque service.' },
  { icone: Presentation, titre: 'Présentations', texte: 'Restitutions et supports de revue, prêts à projeter.' },
  { icone: Image, titre: 'Visuels', texte: 'Affiches et supports de sensibilisation, à télécharger tels quels.' },
  { icone: Megaphone, titre: 'Spots audio', texte: 'Messages radio dits par une voix de synthèse.' },
  { icone: Video, titre: 'Vidéos', texte: 'Capsules de sensibilisation et restitutions animées, montées automatiquement.' },
];

const MEMOIRE = [
  { icone: ShieldCheck, titre: 'Sources hiérarchisées', texte: 'Un document officiel prime sur une note de travail, le plus récent sur le plus ancien.' },
  { icone: CalendarClock, titre: 'Faits datés', texte: 'Chaque fait garde sa période et le document dont il vient.' },
  { icone: BookOpen, titre: 'Contradictions arbitrées', texte: "Quand deux sources divergent, un administrateur tranche, et l'arbitrage vaut pour tous." },
];

/**
 * Page d'accueil, avant connexion.
 *
 * OmnIA est une plateforme destinée à plusieurs organisations ; le PEV de
 * Guinée en est le premier déploiement. La page présente donc la plateforme,
 * cite le PEV comme référence, et mène à la connexion, après laquelle tout est
 * aux couleurs de l'organisation. Aucun chiffre ni témoignage inventé.
 */
const HomePage: React.FC = () => {
  const connecte = authManager.isAuthenticated();
  const entree = connecte
    ? { href: '/workspace/conversations', libelle: 'Ouvrir OmnIA' }
    : { href: '/login', libelle: 'Se connecter' };

  const boutonPrincipal = (
    <Link
      to={entree.href}
      className="inline-flex items-center gap-2 rounded-full bg-eg-sauge px-5 py-2.5 text-[14px] font-medium
        text-white shadow-[0_6px_20px_-6px_rgba(0,0,0,0.35)] transition-colors hover:bg-[#5E8276]"
    >
      {entree.libelle}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
  const boutonSecondaire = 'inline-flex items-center rounded-full px-5 py-2.5 text-[14px] font-medium text-white outline outline-1 -outline-offset-1 outline-white/30 transition-colors hover:bg-white/10';
  const fondPetrole = 'bg-[linear-gradient(165deg,#365B6D_0%,#2E5061_55%,#284756_100%)] text-white';

  return (
    <div className="flex min-h-screen flex-col bg-eg-fond text-eg-encre">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#365B6D]/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1120px] items-center gap-3 px-4 py-3 sm:px-6">
          <img src="/logo-omnia-blanc.svg" alt="OmnIA" className="h-7 w-auto" />
          <Link
            to={entree.href}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[13px]
              font-medium text-eg-accent transition-colors hover:bg-eg-voile"
          >
            {entree.libelle}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* Ouverture */}
        <section className={`relative overflow-hidden ${fondPetrole}`}>
          <div
            aria-hidden
            className="pointer-events-none absolute -top-48 left-1/2 h-[560px] w-[960px] -translate-x-1/2 rounded-full
              bg-[radial-gradient(closest-side,rgba(108,146,134,0.45),transparent)]"
          />
          <div className="relative mx-auto max-w-[1120px] px-4 pb-16 pt-16 text-center sm:px-6 sm:pt-24">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[12px] font-medium text-white/90 outline outline-1 -outline-offset-1 outline-white/15">
              <span className="h-1.5 w-1.5 rounded-full bg-[#9CC0B3]" />
              Plateforme d'agents IA pour les organisations
            </div>
            <h1 className="mx-auto mt-6 max-w-[22ch] text-[36px] font-semibold leading-[1.08] tracking-tight sm:text-[52px]">
              Des agents IA qui travaillent avec la mémoire de votre organisation
            </h1>
            <p className="mx-auto mt-5 max-w-[62ch] text-[15.5px] leading-relaxed text-white/75 sm:text-[17px]">
              OmnIA réunit des agents spécialisés : rédaction, budgets, analyse de données,
              communication, réunions. Ils s'appuient sur les documents que votre organisation a
              validés, et chaque organisation a son espace, ses agents et sa mémoire.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {boutonPrincipal}
              <a href="#productions" className={boutonSecondaire}>Ce que font les agents</a>
            </div>
            <p className="mt-5 text-[12.5px] text-white/60">
              Une plateforme conçue et réalisée par <span className="font-medium text-white/85">Efficience Globale</span>
            </p>

            {/* Aperçu du point de départ : on écrit, l'agent est choisi. */}
            <div className="mx-auto mt-14 max-w-[680px] px-1 pb-2 text-left text-[11.5px] font-medium text-white/60">
              Exemple au PEV Guinée
            </div>
            <div className="mx-auto max-w-[680px] rounded-2xl bg-white p-4 text-left text-eg-encre shadow-[0_24px_60px_-20px_rgba(0,0,0,0.45)] sm:p-5">
              <p className="text-[14.5px] leading-relaxed text-eg-encre">
                Prépare les TDR de l'atelier de microplanification du district de Kankan, trois
                jours, avec le budget
              </p>
              <div className="mt-4 flex items-center gap-2">
                <span className="inline-flex min-w-0 items-center gap-1.5 truncate rounded-full bg-eg-accent/[0.09] px-2.5 py-1 text-[12px] font-medium text-eg-accent">
                  @ TDR d'activités PEV avec budget
                </span>
                <span className="hidden text-[12px] text-eg-estompe sm:inline">choisi d'après la demande</span>
                <span className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-eg-ombre text-white">
                  <ArrowUp className="h-4 w-4" />
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Trois façons de commencer */}
        <section className="bg-eg-sauge/[0.10]">
          <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6 sm:py-20">
            <h2 className="text-[24px] font-semibold tracking-tight text-eg-accent sm:text-[28px]">Trois façons de commencer</h2>
            <p className="mt-2 max-w-[60ch] text-[14.5px] text-eg-gris">
              Le menu de la plateforme s'ouvre sur elles : discuter, produire un support pour le
              public, ou confier une tâche qui revient chaque mois.
            </p>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {DEPARTS.map(({ icone: Icone, titre, texte }) => (
                <div key={titre} className={`p-6 ${carte}`}>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-eg-accent text-white">
                    <Icone className="h-[18px] w-[18px]" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-4 text-[15.5px] font-medium text-eg-accent">{titre}</h3>
                  <p className="mt-1.5 text-[13.5px] leading-relaxed text-eg-gris">{texte}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Productions */}
        <section id="productions" className="scroll-mt-16">
          <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6 sm:py-20">
            <h2 className="text-[24px] font-semibold tracking-tight text-eg-accent sm:text-[28px]">Ce que rendent les agents</h2>
            <p className="mt-2 max-w-[60ch] text-[14.5px] text-eg-gris">
              Des documents prêts à être relus et transmis, dans les formats de l'organisation.
            </p>
            <div className="mt-8 grid gap-px overflow-hidden rounded-2xl bg-eg-bordure outline outline-1 -outline-offset-1 outline-black/[0.07] sm:grid-cols-2 lg:grid-cols-4">
              {PRODUCTIONS.map(({ icone: Icone, titre, texte }) => (
                <div key={titre} className="bg-eg-blanc p-5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-eg-sauge text-white">
                    <Icone className="h-[17px] w-[17px]" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-3 text-[14px] font-medium">{titre}</h3>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-eg-gris">{texte}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mémoire */}
        <section className={fondPetrole}>
          <div className="mx-auto grid max-w-[1120px] items-center gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2">
            <div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-eg-sauge text-white">
                <BookOpen className="h-[18px] w-[18px]" strokeWidth={1.8} />
              </div>
              <h2 className="mt-4 text-[24px] font-semibold tracking-tight sm:text-[28px]">La mémoire de l'organisation</h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-white/75">
                Plans, rapports annuels, lignes directrices : les administrateurs de l'organisation
                versent les documents officiels, la plateforme en extrait les faits et signale
                ceux qui se contredisent. Les agents s'appuient sur cette mémoire, et un chiffre
                que rien n'établit est écrit « à renseigner », jamais inventé.
              </p>
            </div>
            <div className="rounded-2xl bg-white p-5 text-eg-encre shadow-[0_24px_60px_-20px_rgba(0,0,0,0.45)]">
              {MEMOIRE.map(({ icone: Icone, titre, texte }, index) => (
                <div key={titre} className={`flex gap-3.5 py-3.5 ${index > 0 ? 'border-t border-eg-bordure' : ''}`}>
                  <Icone className="mt-0.5 h-4 w-4 shrink-0 text-eg-sauge" strokeWidth={1.8} />
                  <div>
                    <div className="text-[13.5px] font-medium">{titre}</div>
                    <div className="mt-0.5 text-[12.5px] leading-relaxed text-eg-gris">{texte}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Référence */}
        <section>
          <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6 sm:py-20">
            <div className={`grid items-center gap-8 p-6 sm:p-8 md:grid-cols-[auto_1fr] ${carte}`}>
              <div className="flex items-center justify-center gap-5">
                <img src="/logos/pev.png" alt="Programme Élargi de Vaccination, Guinée" className="h-14 w-auto object-contain" />
                <img src="/logos/ministere-sante.png" alt="Ministère de la Santé et de l'Hygiène Publique" className="h-14 w-auto object-contain" />
              </div>
              <div>
                <div className="text-[11.5px] font-medium uppercase tracking-wider text-eg-sauge">Premier déploiement</div>
                <h2 className="mt-1.5 text-[20px] font-semibold tracking-tight text-eg-accent sm:text-[22px]">
                  Programme Élargi de Vaccination de Guinée
                </h2>
                <p className="mt-2 text-[14px] leading-relaxed text-eg-gris">
                  Au sein du Ministère de la Santé et de l'Hygiène Publique, les équipes du PEV
                  utilisent OmnIA pour leurs TDR et budgets, l'analyse des données DHIS2, leurs
                  supports de sensibilisation et leurs comptes rendus de réunion.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Accès */}
        <section className={fondPetrole}>
          <div className="mx-auto max-w-[1120px] px-4 py-16 text-center sm:px-6 sm:py-20">
            <h2 className="text-[22px] font-semibold tracking-tight sm:text-[26px]">Accès sur invitation</h2>
            <p className="mx-auto mt-2 max-w-[58ch] text-[14.5px] text-white/75">
              Les comptes sont créés par l'administrateur de chaque organisation. Vous souhaitez
              OmnIA pour la vôtre ? Écrivez à l'équipe OmnIA.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              {boutonPrincipal}
              {CONTACT && (
                <a
                  href={`mailto:${CONTACT}?subject=${encodeURIComponent('OmnIA pour notre organisation')}`}
                  className={boutonSecondaire}
                >
                  Contacter l'équipe OmnIA
                </a>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-[#284756]">
        <div className="mx-auto flex max-w-[1120px] flex-wrap items-center gap-3 px-4 py-5 text-[12px] text-white/60 sm:px-6">
          <img src="/logo-omnia-blanc.svg" alt="OmnIA" className="h-5 w-auto" />
          <span>Plateforme d'agents IA, réalisée par Efficience Globale</span>
          <span className="ml-auto">© {new Date().getFullYear()} OmnIA</span>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
